import { useState, useCallback } from 'react';
import type React from 'react';
import { arrayMove } from '@dnd-kit/sortable';

import {
  calcMidpointOrder,
  showReorderError,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-helpers';
import {
  applyRegimePlacement,
  buildGroupsWithRegimes,
  isSidebarDragData,
  type SidebarDragData,
  type SidebarDragEvent,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-drag';
import type {
  RegimeGroup,
  RegimeItem,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-types';

interface UpdateRegimeMutation {
  mutateAsync(data: unknown): Promise<unknown>;
}

interface UseRegimesSidebarDragActionsParams {
  userId: string;
  modelId: string | number;
  setActiveDragData: React.Dispatch<
    React.SetStateAction<SidebarDragData | null>
  >;
  localGroups: RegimeGroup[];
  localRegimes: RegimeItem[];
  setLocalRegimes: React.Dispatch<React.SetStateAction<RegimeItem[]>>;
  pendingRegimeIds: Set<string | number>;
  addPendingRegime: (regimeId: string | number) => void;
  removePendingRegime: (regimeId: string | number) => void;
  updateRegimeMut: UpdateRegimeMutation;
  expandedGroupIds: Record<string | number, boolean>;
}

interface UseRegimesSidebarDragActionsResult {
  handleDragStart: (event: SidebarDragEvent) => void;
  handleDragOver: (event: SidebarDragEvent) => void;
  handleDragEnd: (event: SidebarDragEvent) => void;
  handleDragCancel: () => void;
  handleRegimeReorder: (
    regimeId: string | number,
    direction: 'up' | 'down'
  ) => void;
}

export const useRegimesSidebarDragActions = ({
  userId,
  modelId,
  setActiveDragData,
  localGroups,
  localRegimes,
  setLocalRegimes,
  pendingRegimeIds,
  addPendingRegime,
  removePendingRegime,
  updateRegimeMut,
  expandedGroupIds,
}: UseRegimesSidebarDragActionsParams): UseRegimesSidebarDragActionsResult => {
  const [dragStartRegimes, setDragStartRegimes] = useState<RegimeItem[] | null>(
    null
  );

  const handleRegimeDragOver = useCallback(
    (activeData: SidebarDragData, overData: SidebarDragData) => {
      const targetGroupId = overData.groupId;

      if (targetGroupId !== undefined && !expandedGroupIds[targetGroupId])
        return;

      const nextRegimes = applyRegimePlacement(
        localRegimes,
        localGroups,
        activeData.entityId,
        overData
      );

      if (!nextRegimes) return;

      setLocalRegimes(nextRegimes);
    },
    [expandedGroupIds, localGroups, localRegimes, setLocalRegimes]
  );

  const clearDragState = useCallback(() => {
    setActiveDragData(null);
    setDragStartRegimes(null);
  }, []);

  const handleDragStart = useCallback(
    (event: SidebarDragEvent) => {
      const activeData = event.active.data.current;

      if (!isSidebarDragData(activeData)) return;
      if (
        activeData.type === 'regime' &&
        pendingRegimeIds.has(activeData.entityId)
      )
        return;

      setActiveDragData(activeData);
      setDragStartRegimes(localRegimes);
    },
    [localRegimes, pendingRegimeIds]
  );

  const handleDragOver = useCallback(
    (event: SidebarDragEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const activeData = active.data.current;
      const overData = over.data.current;

      if (!isSidebarDragData(activeData) || !isSidebarDragData(overData))
        return;
      if (
        activeData.type === 'regime' &&
        pendingRegimeIds.has(activeData.entityId)
      )
        return;

      if (activeData.type === 'regime') {
        handleRegimeDragOver(activeData, overData);
      }
    },
    [handleRegimeDragOver, pendingRegimeIds]
  );

  const handleDragCancel = useCallback(() => {
    if (dragStartRegimes) {
      setLocalRegimes(dragStartRegimes);
    }

    clearDragState();
  }, [clearDragState, dragStartRegimes, setLocalRegimes]);

  const handleRegimeMove = useCallback(
    (activeData: SidebarDragData) => {
      const originalRegimes = dragStartRegimes ?? localRegimes;
      const originalRegime = originalRegimes.find(
        (r) => r.id === activeData.entityId
      );
      const activeRegime = localRegimes.find(
        (r) => r.id === activeData.entityId
      );

      if (!originalRegime || !activeRegime) return;
      if (pendingRegimeIds.has(activeRegime.id)) return;

      const targetGroup = buildGroupsWithRegimes(
        localGroups,
        localRegimes,
        true
      ).find((g) => g.id === activeRegime.groupId);

      if (!targetGroup) return;

      const targetIndex = targetGroup.regimes.findIndex(
        (r) => r.id === activeRegime.id
      );
      const didChangeGroup = originalRegime.groupId !== activeRegime.groupId;
      const didChangeOrder =
        originalRegime.sortOrder !== activeRegime.sortOrder;

      if (targetIndex < 0 || (!didChangeGroup && !didChangeOrder)) return;

      const originalRegimesById = new Map(
        originalRegimes.map((regime) => [regime.id, regime])
      );
      const persistedOrderItems = targetGroup.regimes.map(
        (regime) => originalRegimesById.get(regime.id) ?? regime
      );
      const newSortOrder = calcMidpointOrder(persistedOrderItems, targetIndex);
      const previousRegimes = dragStartRegimes ?? localRegimes;
      const updateData = didChangeGroup
        ? { groupId: activeRegime.groupId, sortOrder: newSortOrder }
        : { sortOrder: newSortOrder };

      addPendingRegime(activeRegime.id);
      setLocalRegimes(
        originalRegimes.map((r) =>
          r.id === activeRegime.id
            ? { ...r, groupId: activeRegime.groupId, sortOrder: newSortOrder }
            : r
        )
      );

      updateRegimeMut
        .mutateAsync({
          userId,
          modelId,
          regimeId: activeRegime.id,
          data: updateData,
        })
        .catch((error) => {
          setLocalRegimes(previousRegimes);
          showReorderError(error?.data?.detail);
        })
        .finally(() => {
          removePendingRegime(activeRegime.id);
        });
    },
    [
      addPendingRegime,
      dragStartRegimes,
      localGroups,
      localRegimes,
      pendingRegimeIds,
      removePendingRegime,
      setLocalRegimes,
      updateRegimeMut,
      userId,
      modelId,
    ]
  );

  // Single-pointer/keyboard alternative to dragging a regime within its group
  // (WCAG 2.5.7) — reuses the same calcMidpointOrder + updateRegimeMut
  // reorder logic that handleRegimeMove uses for drag-end persistence.
  const handleRegimeReorder = useCallback(
    (regimeId: string | number, direction: 'up' | 'down') => {
      if (pendingRegimeIds.has(regimeId)) return;

      const regime = localRegimes.find((r) => r.id === regimeId);

      if (!regime) return;

      const groupRegimes = buildGroupsWithRegimes(
        localGroups,
        localRegimes,
        true
      ).find((g) => g.id === regime.groupId)?.regimes;

      if (!groupRegimes) return;

      const idx = groupRegimes.findIndex((r) => r.id === regimeId);
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;

      if (idx < 0 || targetIdx < 0 || targetIdx >= groupRegimes.length) return;

      const reordered = arrayMove(groupRegimes, idx, targetIdx);
      const newSortOrder = calcMidpointOrder(reordered, targetIdx);
      const previousRegimes = localRegimes;

      addPendingRegime(regimeId);
      setLocalRegimes((prev) =>
        prev.map((r) =>
          r.id === regimeId ? { ...r, sortOrder: newSortOrder } : r
        )
      );

      updateRegimeMut
        .mutateAsync({
          userId,
          modelId,
          regimeId,
          data: { sortOrder: newSortOrder },
        })
        .catch((error) => {
          setLocalRegimes(previousRegimes);
          showReorderError(error?.data?.detail);
        })
        .finally(() => {
          removePendingRegime(regimeId);
        });
    },
    [
      addPendingRegime,
      localGroups,
      localRegimes,
      pendingRegimeIds,
      removePendingRegime,
      setLocalRegimes,
      updateRegimeMut,
      userId,
      modelId,
    ]
  );

  const handleDragEnd = useCallback(
    (event: SidebarDragEvent) => {
      const { active, over } = event;

      if (!over) {
        if (dragStartRegimes) {
          setLocalRegimes(dragStartRegimes);
        }

        clearDragState();

        return;
      }

      const activeData = active.data.current;
      const overData = over.data.current;

      if (!isSidebarDragData(activeData) || !isSidebarDragData(overData)) {
        clearDragState();

        return;
      }
      if (
        activeData.type === 'regime' &&
        pendingRegimeIds.has(activeData.entityId)
      ) {
        clearDragState();

        return;
      }

      if (active.id === over.id && activeData.type !== 'regime') {
        clearDragState();

        return;
      }

      if (activeData.type === 'regime') {
        handleRegimeMove(activeData);
      }

      clearDragState();
    },
    [
      clearDragState,
      dragStartRegimes,
      handleRegimeMove,
      pendingRegimeIds,
      setLocalRegimes,
    ]
  );

  return {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    handleRegimeReorder,
  };
};
